import { Badge, Box, Tooltip, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { ReactNode } from 'react';
import { IntlShape } from 'react-intl';
import { assign } from 'xstate';

import { MachineContext } from '../../MachineContext';
import { theme } from '../../theme/theme';
import {
  checkMessageObject,
  getStringValuesParam,
  getValuesFromParam,
  translateWithFallback,
} from '../../util';
import { BaseComponent } from '../Component';

export interface CoachMessageComponent extends BaseComponent {
  type: 'coachMessage';
  props: CoachMessageProps;
}
export interface CoachMessageProps {
  message: string | ReactNode | Array<string | ReactNode>;
  showAiBadge?: boolean;
  aiTooltipText?: string;
}

export const CoachMessage: React.FC<CoachMessageProps> = ({
  message,
  showAiBadge = false,
  aiTooltipText = '',
}) => {
  //
  // getReactMapping transforms the message object into a react element
  // This is necessary for these cases
  //
  // 1. message is a string (the function would be unnecessary in this case)
  // 2. message is an array of strings and JS objects that were derived from
  //    the string by the AI or by manual formatting like <i>hello</i>
  // 3. message is an array of strings and JS objects that are nested
  //    <strong>this is: <i>hello</i></strong>
  //
  const getReactMapping = (m: any, index: number = 0): ReactNode => {
    if (Array.isArray(m)) {
      return m.map((element, index) => getReactMapping(element, index));
    } else {
      if (typeof m === 'string') {
        return m;
      } else {
        if (typeof m === 'object' && m !== null) {
          const { type, props } = m;

          // we can't modify the props object as it is read-only in
          // React. We thus have to create a new object
          let newProps = { ...props, key: index };

          if (props && props.children && Array.isArray(props.children)) {
            newProps = {
              ...newProps,
              children: props.children.map(getReactMapping),
            };
          }

          return React.createElement(type, newProps);
        } else {
          return null;
        }
      }
    }
  };

  //
  // The message is rendered with the getReactMapping function
  //
  return (
    <>
      <Badge
        badgeContent={
          <Tooltip title={aiTooltipText} placement="top" arrow>
            <Typography variant="caption">AI</Typography>
          </Tooltip>
        }
        color="primary"
        invisible={!showAiBadge}
      >
        <Box
          sx={{
            display: 'block',
            padding: '15px',
            borderRadius: '6px 6px 6px 0',
            backgroundColor: '#f2f2f2' /*coach-message-background-color*/,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <Typography>{getReactMapping(message)}</Typography>
        </Box>
      </Badge>
    </>
  );
};

//
// render..Action is called by state machine in chatbot to push the control on
// the satack of elements to be rendered
//
export const renderCoachMessageAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    const getValuesFrom: string[] | undefined = getValuesFromParam(payload);
    const varsForString: Record<string, any> | undefined = getStringValuesParam(
      payload,
      context
    );
    const showAiBadge =
      payload.showAiBadge &&
      payload.showAiBadge !== undefined &&
      payload.showAiBadge !== null
        ? payload.showAiBadge
        : false;

    // if no variables are passed
    if (
      getValuesFrom === undefined ||
      getValuesFrom === null ||
      (Array.isArray(getValuesFrom) && getValuesFrom.length === 0)
    ) {
      // check for undefined.
      // NOTE: do not write if(payload.message) because this ignores
      // the valid value payload.message=0

      if (payload.message !== undefined && payload.message !== null) {
        newContext.widgetData.push({
          type: 'coachMessage',
          temporary: actionMetadata.action.temporary ?? false,
          props: {
            message: translateWithFallback(
              intl,
              payload.message,
              varsForString
            ),
            showAiBadge: showAiBadge ?? false,
            aiTooltipText: payload.helperText
              ? translateWithFallback(intl, payload.helperText)
              : translateWithFallback(
                  intl,
                  'aicoachmessage.aitooltip' // s. ProtectedTexts.tsx
                ),
          },
        });
      }
    } else {
      // variables are set ...
      const messages: Array<any> = getValuesFrom
        .filter(
          (varname: string) =>
            varname !== undefined && varname !== null && varname.trim() !== ''
        )
        .map((varname: string) => {
          //
          const content = context.userData[varname];
          // PROD-1993 - auto mark AI content
          const aiBadge = context.userData[varname + '.isAi'];

          if (typeof content === 'string') {
            return { message: content, aiBadge: aiBadge };
          } else {
            if (Array.isArray(content)) {
              const contents: string[] = content
                .flat()
                .map((value: any) => checkMessageObject(value, 'value', intl));
              return contents.map((con: string) => {
                return { message: con, aiBadge: aiBadge };
              });
            }
          }
          return { message: '', aiBadge: aiBadge };
        })
        .flat();

      messages.forEach((message: any) => {
        newContext.widgetData.push({
          type: 'coachMessage',
          temporary: actionMetadata.action.temporary ?? false,
          props: {
            message: message.message,
            showAiBadge: message.aiBadge ?? showAiBadge ?? false,
            aiTooltipText: payload.helperText
              ? translateWithFallback(intl, payload.helperText)
              : translateWithFallback(
                  intl,
                  'aicoachmessage.aitooltip' // s. ProtectedTexts.tsx
                ),
          },
        });
      });
    }
    return newContext;
  });
};
